import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, AlertTriangle, Clock, Gauge, Save, Search, ShieldAlert, HelpCircle, Wrench } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { StatCard } from "@/components/kit";
import { AdminAiUsage } from "@/components/AdminAiUsage";

const STATUS_STYLE = {
  ok: "text-volt border-volt/30", failed: "text-red-400 border-red-400/30", not_configured: "text-red-400 border-red-400/30",
  rate_limited: "text-amber-400 border-amber-400/30", disabled: "text-zinc-400 border-zinc-700",
};

function Providers({ stats }) {
  const names = { gemini: "Google Gemini", openai: "OpenAI", emergent: "Emergent key" };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="aihelp-providers">
      <h3 className="font-semibold mb-3">Providers <span className="text-xs text-zinc-500 font-normal">tried in this order · Gemini model: {stats.model}</span></h3>
      <div className="grid sm:grid-cols-3 gap-3">
        {Object.entries(names).map(([k, label]) => {
          const on = stats.providers?.[k]; const use = stats.provider_usage?.[k];
          return (
            <div key={k} data-testid={`aihelp-provider-${k}`} className="bg-black/30 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm">{on ? <CheckCircle2 className="w-4 h-4 text-volt" /> : <AlertTriangle className="w-4 h-4 text-zinc-600" />}{label}</div>
              <div className="text-xs text-zinc-500 mt-1">{on ? (use ? `${use.answers} answers · avg ${(use.avg_latency_ms / 1000).toFixed(1)}s` : "Configured · no answers yet") : "No key set"}</div>
            </div>
          );
        })}
      </div>
      {stats.last_failure && <p className="text-xs text-red-400/80 mt-3 break-words">Last failure {new Date(stats.last_failure.at).toLocaleString()}: {stats.last_failure.errors?.join(" | ") || "unknown"}</p>}
    </div>
  );
}

function Daily({ days }) {
  const max = Math.max(1, ...days.map((d) => d.ok + d.failed + d.rate_limited));
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="aihelp-daily">
      <h3 className="font-semibold mb-4">Last 7 days</h3>
      <div className="flex items-end gap-3 h-32">
        {days.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.ok} ok · ${d.failed} failed · ${d.rate_limited} rate-limited`}>
            <div className="w-full flex flex-col-reverse rounded overflow-hidden" style={{ height: `${((d.ok + d.failed + d.rate_limited) / max) * 100}%`, minHeight: 2 }}>
              <div className="bg-volt" style={{ flex: d.ok }} /><div className="bg-red-400" style={{ flex: d.failed }} /><div className="bg-amber-400" style={{ flex: d.rate_limited }} />
            </div>
            <span className="text-[10px] text-zinc-500">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings({ initial, onSaved }) {
  const [s, setS] = useState(initial);
  const num = (k, min, max) => (
    <input data-testid={`aihelp-${k}`} type="number" min={min} max={max} value={s[k]} onChange={(e) => setS({ ...s, [k]: +e.target.value })} className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
  );
  const save = async () => {
    try { await api.put("/admin/ai-help/settings", s); toast.success("AI Help settings saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4" data-testid="aihelp-settings">
      <h3 className="font-semibold">Settings</h3>
      <label className="flex items-center justify-between text-sm"><span>AI Help enabled</span>
        <input data-testid="aihelp-enabled" type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} className="w-5 h-5 accent-volt" /></label>
      <div><label className="text-xs uppercase tracking-wider text-zinc-500">Questions per user / hour</label>{num("per_user_per_hour", 1, 200)}</div>
      <div><label className="text-xs uppercase tracking-wider text-zinc-500">Cooldown between questions (s)</label>{num("cooldown_seconds", 0, 3600)}</div>
      <div><label className="text-xs uppercase tracking-wider text-zinc-500">Keep conversations (days)</label>{num("retention_days", 1, 3650)}</div>
      <button data-testid="aihelp-save" onClick={save} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save</button>
      <p className="text-xs text-zinc-500">Knowledge base: <code>backend/ai_help_kb.md</code> (restart not needed — read on every question).</p>
    </div>
  );
}

function Logs() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/admin/ai-help/logs", { params: { q: q || undefined, status: status || undefined, limit: 50 } })
    .then((r) => setRows(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); else toast.error(errMsg(e)); });
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (denied) return <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-sm text-zinc-400 flex items-center gap-2" data-testid="aihelp-logs-denied"><ShieldAlert className="w-4 h-4" /> Viewing conversations requires the <code>aihelp.logs</code> permission.</div>;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="aihelp-logs">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <h3 className="font-semibold mr-auto">Conversations <span className="text-xs text-zinc-500 font-normal">· views are audit-logged</span></h3>
        <select data-testid="aihelp-log-status" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-black border border-zinc-800 rounded-md px-2 py-1.5 text-sm">
          <option value="">All</option><option value="ok">Answered</option><option value="failed">Failed</option><option value="rate_limited">Rate-limited</option><option value="not_configured">Not configured</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <input data-testid="aihelp-log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search question, user, server…" className="bg-black border border-zinc-800 rounded-md px-3 py-1.5 text-sm w-56" />
          <button data-testid="aihelp-log-search-btn" className="bg-zinc-800 rounded-md px-3"><Search className="w-4 h-4" /></button>
        </form>
      </div>
      {rows === null ? <p className="text-sm text-zinc-500">Loading…</p> : rows.length === 0 ? <p className="text-sm text-zinc-500" data-testid="aihelp-logs-empty">No conversations yet.</p> : (
        <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
          {rows.map((r, i) => (
            <div key={r.request_id} data-testid={`aihelp-log-${i}`} className="bg-black/30 rounded-lg p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500 mb-1">
                <span className={`border rounded px-1.5 py-0.5 ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                {r.status === "ok" && r.confident === false && <span className="border border-amber-400/30 text-amber-400 rounded px-1.5 py-0.5">low confidence</span>}
                <span className="text-zinc-300">{r.username || r.user_id}</span><span>· {r.guild_name || "DM"}</span>
                {r.provider && <span>· {r.provider}</span>}{r.latency_ms != null && <span>· {(r.latency_ms / 1000).toFixed(1)}s</span>}
                <span className="ml-auto">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              {r.tools_used?.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-1.5" data-testid={`aihelp-log-tools-${i}`}>
                  <span className="text-[11px] text-zinc-500 flex items-center gap-1"><Wrench className="w-3 h-3" /> Tools used:</span>
                  {r.tools_used.map((t, j) => (
                    <span key={j} data-testid={`aihelp-log-tool-${i}-${j}`} title={t.ok ? "Succeeded" : "Failed / not permitted"}
                      className={`text-[11px] border rounded px-1.5 py-0.5 ${t.ok ? "border-sky-400/30 text-sky-300" : "border-red-400/30 text-red-400"}`}>
                      {t.name.replace(/^get_/, "")} · {t.username || t.user_id}
                    </span>
                  ))}
                </div>
              )}
              {r.question && <p className="text-zinc-200 flex gap-2"><HelpCircle className="w-4 h-4 shrink-0 mt-0.5 text-zinc-500" />{r.question}</p>}
              {r.answer && <p className="text-zinc-400 whitespace-pre-wrap mt-2 pl-6 border-l border-zinc-800">{r.answer}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminAiHelp() {
  const [stats, setStats] = useState(null);
  const load = () => api.get("/admin/ai-help/stats").then((r) => setStats(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!stats) return <p className="text-zinc-500">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="admin-aihelp-panel">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard icon={Sparkles} label="Total requests" value={stats.total} accent="#0EA5E9" />
        <StatCard icon={CheckCircle2} label="Answered" value={stats.ok} accent="#22c55e" />
        <StatCard icon={AlertTriangle} label="Failed" value={stats.failed} accent="#ef4444" />
        <StatCard icon={Clock} label="Rate-limited" value={stats.rate_limited} accent="#f59e0b" />
        <StatCard icon={HelpCircle} label="Low confidence" value={stats.low_confidence} accent="#a855f7" />
        <StatCard icon={Gauge} label="Error rate 24h" value={`${stats.error_rate_24h}%`} accent="#f59e0b" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6"><Providers stats={stats} /><Daily days={stats.daily} /></div>
        <Settings initial={stats.settings} onSaved={load} />
      </div>
      <AdminAiUsage />
      <Logs />
    </div>
  );
}
